/**
 * What a person changed about an extraction, and how to say it.
 *
 * One module for both correction paths: the positions (an extracted product ↔ its
 * `ManualCorrectedProductInfo`) and the header (the company as the extraction produced it ↔
 * the company as it stands now). Both answer the same question — "was wurde korrigiert?" —
 * and they have to answer it in the same words.
 */

/** One field that differs between what the extraction produced and what a person made of it. */
export interface FieldChange {
  key: string;
  /** German field name, as the UI shows it. */
  label: string;
  from: unknown;
  to: unknown;
}

/** Missing, null and empty string all say the same thing to a person: nothing was given. */
const isEmpty = (value: unknown): boolean => value === null || value === undefined || value === '';

/**
 * How a value reads wherever a correction is written out — a change list, a tooltip, the note
 * that records an answered Rückfrage. Empty of any kind is "leer", never "null".
 */
export function formatFieldValue(value: unknown): string {
  if (isEmpty(value)) return 'leer';
  if (typeof value === 'boolean') return value ? 'ja' : 'nein';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  // A luxon DateTime is the only object a corrected value can carry. Recognised by shape
  // rather than by `instanceof`, whose narrowing luxon's typings do not survive here.
  const maybeDate = value as { toFormat?: (format: string) => string };
  if (typeof maybeDate.toFormat === 'function') return maybeDate.toFormat('dd.MM.yyyy');
  return JSON.stringify(value);
}

/** One line per change — for a tooltip, a note or a mail, where there is no markup. */
export function describeFieldChanges(changes: FieldChange[]): string {
  return changes.map(c => `${c.label}: ${formatFieldValue(c.from)} → ${formatFieldValue(c.to)}`).join('\n');
}

const read = (bag: object, key: string): unknown => (bag as Record<string, unknown>)[key];

/**
 * Two values are the same when they say the same thing. Telling null from "" from a missing
 * field apart would report a correction nobody made; and comparing everything else by how it
 * reads means two DateTimes on the same day do not count as a change either.
 */
const sameValue = (before: unknown, after: unknown): boolean =>
  isEmpty(before) || isEmpty(after)
    ? isEmpty(before) && isEmpty(after)
    : formatFieldValue(before) === formatFieldValue(after);

/** The fields of `keys` whose value differs between `before` and `after`, in the order given. */
export function diffFields(
  before: object,
  after: object,
  keys: readonly string[],
  labels: Record<string, string>,
): FieldChange[] {
  const changes: FieldChange[] = [];
  for (const key of keys) {
    const from = read(before, key);
    const to = read(after, key);
    if (sameValue(from, to)) continue;
    changes.push({ key, label: labels[key] ?? key, from, to });
  }
  return changes;
}
